import { useCreateFeeServiceMutation } from '@/pages/Fleet/Businesses/services/fee-services.service';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { feeServiceSchema } from '@/pages/Fleet/Businesses/models/FeeService';
import { fleetPageBreadcrumbs } from '../../../config/breadcrumbs';
import { NavigateLink } from '@/components/navigate/NavigateLinks';
import { NavigateLinks, BackToModuleButton } from '@/components';
import { useServiceFormOptions } from '../../hooks';
import ServiceForm from '../../components/ServiceForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useNotification } from '@/hooks';
import { z } from 'zod';

const newServiceSchema = feeServiceSchema.extend({
  businessId: z.string().min(1, 'El negocio es obligatorio'),
});

type NewServiceSchema = z.infer<typeof newServiceSchema>;

const NewService = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [createFeeService] = useCreateFeeServiceMutation();
  
  const {
    vehicleTypesOptions,
    serviceTypesOptions,
    businessOptions,
    vehicleTypesLoading,
    serviceTypesLoading,
  } = useServiceFormOptions({
    includeBusinesses: true,
    businessSearch: '',
    businessPage: 1,
    businessPageSize: 200,
  });

  const breadcrumbs = useMemo<NavigateLink[]>(() => {
    const base = fleetPageBreadcrumbs(navigate);
    return [
      ...base,
      { label: 'Servicios', onClick: () => navigate('/fleet/services') },
    ];
  }, [navigate]);

  const { handleSubmit, control, formState } = useForm<NewServiceSchema>({
    resolver: zodResolver(newServiceSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      businessId: '',
      vehicleTypeId: '',
      serviceTypeId: '',
      fee: 0,
      min: 0,
    },
  });

  const onSubmit = async (data: NewServiceSchema) => {
    try {
      const payload = {
        businessId: data.businessId,
        vehicleTypeId: data.vehicleTypeId,
        serviceTypeId: data.serviceTypeId,
        fee: data.fee,
        min: data.min,
        validFrom: data.validFrom,
        validTo: data.validTo,
      };
      await createFeeService(payload).unwrap();
      showNotification('Servicio creado correctamente', NotificationSeverity.success);
      navigate('/fleet/services');
    } catch {
      showNotification(
        'Ocurrió un error al crear el servicio. Inténtalo nuevamente.',
        NotificationSeverity.error
      );
    }
  };

  return (
    <section className="flex h-full w-full flex-col gap-[25px]">
      <NavigateLinks actualPage="Agregar servicio" breadcrumbs={breadcrumbs} />
      <div className="flex justify-end gap-5">
        <BackToModuleButton breadcrumbs={breadcrumbs} />
      </div>
      <div className="w-full flex rounded-[5px] shadow-[0_0_4px_rgba(0,0,0,0.1)]">
        <ServiceForm<NewServiceSchema>
          mode="create"
          control={control}
          handleSubmit={handleSubmit(onSubmit)}
          vehicleTypesOptions={vehicleTypesOptions}
          serviceTypesOptions={serviceTypesOptions}
          businessOptions={businessOptions || []}
          vehicleTypesLoading={vehicleTypesLoading}
          serviceTypesLoading={serviceTypesLoading}
          title="Ingresar datos del servicio"
          onCancel={() => navigate('/fleet/services')}
          submitLabel="Crear servicio"
          isSubmitting={formState.isSubmitting}
          submitDisabled={!formState.isValid}
        />
      </div>
    </section>
  );
};

export default NewService;
